import React, { useEffect, useRef, useState } from 'react';
import contactApi from '../services/contactApi';
import linkedinImportApi from '../services/linkedinImportApi';
import Icon from './Icon';
import './LinkedInImportTab.css';

const isCsv = (file) => file.type === 'text/csv' || /\.csv$/i.test(file.name);

const plural = (n, word, pluralWord) => `${n} ${n === 1 ? word : (pluralWord || `${word}s`)}`;

// Aggregated across every advisor group in the response (usually just one)
const totalsOf = (response) => response.advisors.reduce((sum, summary) => ({
  rows: sum.rows + summary.rows,
  already_on_file: sum.already_on_file + summary.already_on_file,
  added: sum.added + summary.added,
  refreshed: sum.refreshed + summary.refreshed
}), { rows: 0, already_on_file: 0, added: 0, refreshed: 0 });

const Stat = ({ label, value }) => (
  <div className="li-import__stat">
    <dt>{label}</dt>
    <dd>{value}</dd>
  </div>
);

const PHASE_LABELS = { titles: 'Standardizing job titles', importing: 'Importing connections' };

/** A queued or running job's progress, polled from GET /api/v2/ingest/jobs/{id}. */
const ImportProgress = ({ progress }) => {
  if (!progress) return null;
  const { phase, processed, total } = progress;
  const label = PHASE_LABELS[phase] || 'Starting';
  const known = Boolean(total);
  const pct = known ? Math.max(4, Math.min(100, Math.round((processed / total) * 100))) : 100;
  return (
    <div className="li-import__progress" role="status">
      <div className="li-import__progress-label">
        {label}{known && `: ${processed.toLocaleString()} of ${total.toLocaleString()} rows`}
      </div>
      <div className={`li-import__progress-track${known ? '' : ' li-import__progress-track--indeterminate'}`}>
        <div className="li-import__progress-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

/** One advisor's group within an import response: counts plus target-account and lookalike signals. */
const AdvisorSummaryCard = ({ summary, advisorCount }) => {
  const skipped = summary.skipped.no_name + summary.skipped.no_profile_url_or_email + summary.skipped.repeats;
  const newlyKnown = summary.known_on_target_lists.filter((person) => person.new).length;
  return (
    <div className="li-import__advisor">
      {advisorCount > 1 && <h4 className="li-import__advisor-name">{summary.advisor}</h4>}
      <dl className="li-import__stats">
        <Stat label="Rows read" value={summary.rows} />
        <Stat label="Already on file" value={summary.already_on_file} />
        <Stat label="Added" value={summary.added} />
        <Stat label="Refreshed" value={summary.refreshed} />
        {summary.missing_from_export > 0 && <Stat label="On file, not in this export (kept)" value={summary.missing_from_export} />}
        {skipped > 0 && <Stat label="Skipped" value={skipped} />}
      </dl>

      {summary.companies_created > 0 && (
        <p className="li-import__note">
          {plural(summary.companies_created, 'new company', 'new companies')} created
          {summary.placeholder_companies_created > 0 && ` (${plural(summary.placeholder_companies_created, 'placeholder employer')}, e.g. Self-employed)`}.
        </p>
      )}

      {summary.connections_at_target_accounts > 0 && (
        <p className="li-import__note">
          {plural(summary.connections_at_target_accounts, 'connection')} at {plural(summary.target_accounts.length, 'target account')}
          {summary.target_accounts.length > 0 && (
            <>: {summary.target_accounts.slice(0, 5).map((account) => `${account.account} (${account.connections})`).join(', ')}
              {summary.target_accounts.length > 5 && `, +${summary.target_accounts.length - 5} more`}</>
          )}
        </p>
      )}

      {summary.known_on_target_lists.length > 0 && (
        <p className="li-import__note">
          {summary.advisor} knows {plural(summary.known_on_target_lists.length, 'person', 'people')} on target-account lists
          {newlyKnown > 0 && ` (${newlyKnown} newly linked by this import)`}.
        </p>
      )}

      {summary.lookalike_companies.length > 0 && (
        <p className="li-import__warning">
          <Icon name="alert-triangle" />
          {plural(summary.lookalike_companies.length, 'new company', 'new companies')} named like a target-account company,
          not linked ({summary.lookalike_companies.map((item) => `${item.company} ~ ${item.like.join('; ')}`).join('; ')}).
          If one is the same company, map it in linkedin_company_overrides.json.
        </p>
      )}

      {summary.namesakes.length > 0 && (
        <p className="li-import__warning">
          <Icon name="alert-triangle" />
          {plural(summary.namesakes.length, 'namesake')} left alone (already linked to a different profile):{' '}
          {summary.namesakes.slice(0, 5).map((item) => item.name).join(', ')}
          {summary.namesakes.length > 5 && `, +${summary.namesakes.length - 5} more`}.
        </p>
      )}

      {summary.masked_surnames_ambiguous > 0 && (
        <p className="li-import__warning">
          <Icon name="alert-triangle" />
          {plural(summary.masked_surnames_ambiguous, 'masked list entry', 'masked list entries')} matched more than one connection, left unlinked.
        </p>
      )}
    </div>
  );
};

/** The result of a dry run or a real import: headline, aggregate counts, then each advisor's detail. */
const ImportSummary = ({ response }) => {
  const totals = totalsOf(response);
  return (
    <div className={`li-import__summary${response.dry_run ? '' : ' li-import__summary--applied'}`}>
      <p className="li-import__headline">
        <Icon name={response.dry_run ? 'file-text' : 'check'} />
        {response.message}
      </p>
      {response.backup && <p className="li-import__note">Backed up the database to {response.backup} first.</p>}
      <dl className="li-import__stats li-import__stats--totals">
        <Stat label="Rows read" value={totals.rows} />
        <Stat label="Already on file" value={totals.already_on_file} />
        <Stat label={response.dry_run ? 'Would add' : 'Added'} value={totals.added} />
        <Stat label={response.dry_run ? 'Would refresh' : 'Refreshed'} value={totals.refreshed} />
        <Stat label="Skipped" value={response.error_count} />
      </dl>
      {response.advisors.map((summary) => (
        <AdvisorSummaryCard key={summary.advisor} summary={summary} advisorCount={response.advisors.length} />
      ))}
    </div>
  );
};

const POLL_MS = 2000;

/**
 * Admin-only: import a LinkedIn connections export (Connections.csv from LinkedIn's "Get a copy of
 * your data") and attribute it to an advisor. A dry run previews what would change; applying it
 * writes for real. POST /api/v2/ingest only queues the import (it can be thousands of rows) and
 * returns a job id right away; this polls GET /api/v2/ingest/jobs/{id} for progress and, once it's
 * done, the same result the endpoint used to return directly.
 */
const LinkedInImportTab = () => {
  const inputRef = useRef(null);
  const pollTimer = useRef(null);
  // Bumped whenever a poll loop should stop paying attention (reset, or a newer job started) -
  // cheaper and less error-prone than cancelling the in-flight request itself.
  const pollToken = useRef(0);
  const [file, setFile] = useState(null);
  const [advisor, setAdvisor] = useState('');
  const [advisors, setAdvisors] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | checking | ready | applying | done
  const [progress, setProgress] = useState(null); // { phase, processed, total } while checking/applying
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const stopPolling = () => {
    pollToken.current += 1;
    clearTimeout(pollTimer.current);
  };

  useEffect(() => {
    contactApi.getSources().then(setAdvisors).catch(() => setAdvisors([]));
    return stopPolling;
  }, []);

  // Follows one job to completion: resolves with its result, or rejects with why it failed.
  // Stops touching state as soon as `token` is no longer the active one (superseded or reset).
  const followJob = (jobId, token) => new Promise((resolve, reject) => {
    const tick = async () => {
      if (token !== pollToken.current) return;
      try {
        const job = await linkedinImportApi.jobStatus(jobId);
        if (token !== pollToken.current) return;
        if (job.status === 'done') {
          resolve(job.result);
        } else if (job.status === 'failed') {
          reject(new Error(job.error || 'The import failed'));
        } else {
          setProgress({ phase: job.phase, processed: job.processed_rows, total: job.total_rows });
          pollTimer.current = setTimeout(tick, POLL_MS);
        }
      } catch (err) {
        reject(err);
      }
    };
    tick();
  });

  const reset = () => {
    stopPolling();
    setFile(null);
    setAdvisor('');
    setPhase('idle');
    setProgress(null);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  // Editing the file or advisor after a dry run invalidates it: preview again before applying
  const pickFile = (fileList) => {
    const [chosen] = Array.from(fileList || []);
    if (!chosen) return;
    if (!isCsv(chosen)) {
      setError('Choose a LinkedIn Connections.csv export.');
      return;
    }
    setFile(chosen);
    setPhase('idle');
    setPreview(null);
    setResult(null);
    setError(null);
  };

  const changeAdvisor = (value) => {
    setAdvisor(value);
    setPhase('idle');
    setPreview(null);
    setResult(null);
  };

  const canPreview = Boolean(file) && advisor.trim().length > 0;

  const runPreview = async () => {
    stopPolling();
    const token = pollToken.current;
    setPhase('checking');
    setError(null);
    setProgress(null);
    try {
      const job = await linkedinImportApi.ingest(file, advisor, true);
      const response = await followJob(job.id, token);
      if (token !== pollToken.current) return;
      setPreview(response);
      setPhase('ready');
    } catch (err) {
      if (token !== pollToken.current) return;
      setError(err.message);
      setPhase('idle');
    } finally {
      if (token === pollToken.current) setProgress(null);
    }
  };

  const apply = async () => {
    stopPolling();
    const token = pollToken.current;
    setPhase('applying');
    setError(null);
    setProgress(null);
    try {
      const job = await linkedinImportApi.ingest(file, advisor, false);
      const response = await followJob(job.id, token);
      if (token !== pollToken.current) return;
      setResult(response);
      setPhase('done');
      contactApi.getSources().then(setAdvisors).catch(() => {});
    } catch (err) {
      if (token !== pollToken.current) return;
      setError(err.message);
      setPhase('ready');
    } finally {
      if (token === pollToken.current) setProgress(null);
    }
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (phase !== 'checking' && phase !== 'applying') pickFile(event.dataTransfer.files);
  };

  const busy = phase === 'checking' || phase === 'applying';

  return (
    <div className="adm-tab li-import">
      <p className="li-import__intro">
        Import an advisor's LinkedIn connections export and attribute it to them. Run a preview first,
        then apply it to write the contacts for real.
      </p>

      <div
        className={`li-import__drop${dragging ? ' li-import__drop--over' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          if (!busy) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <Icon name="upload" size={20} />
        <div className="li-import__drop-text">
          {file ? <>Chosen: <strong>{file.name}</strong></> : 'Drop a Connections.csv export here'}
        </div>
        <button type="button" className="rm-button" onClick={() => inputRef.current?.click()} disabled={busy}>
          {file ? 'Choose a different file' : 'Choose CSV'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="li-import__input"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            pickFile(event.target.files);
            event.target.value = '';
          }}
        />
      </div>

      <div className="rm-form__field">
        <label htmlFor="li-import-advisor">Advisor</label>
        <input
          id="li-import-advisor"
          className="rm-input"
          list="li-import-advisors"
          value={advisor}
          placeholder="e.g. Jane Doe"
          disabled={busy}
          onChange={(event) => changeAdvisor(event.target.value)}
        />
        <datalist id="li-import-advisors">
          {advisors.map((source) => (
            <option key={source.name} value={source.name}>{`${source.name} (${source.contact_count})`}</option>
          ))}
        </datalist>
        <p className="rm-form__hint">Whose export this is. Pick an advisor already on file, or type a new name.</p>
      </div>

      {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}

      <div className="li-import__actions">
        {phase !== 'done' && (
          <button type="button" className="rm-button rm-button--primary" disabled={!canPreview || busy} onClick={runPreview}>
            <Icon name={phase === 'checking' ? 'loader' : 'search'} spin={phase === 'checking'} />
            {phase === 'checking' ? 'Checking…' : 'Preview import'}
          </button>
        )}
        {phase === 'ready' && (
          <button type="button" className="rm-button rm-button--primary" onClick={apply}>
            <Icon name="check" /> Apply import
          </button>
        )}
        {phase === 'applying' && (
          <button type="button" className="rm-button rm-button--primary" disabled>
            <Icon name="loader" spin /> Applying…
          </button>
        )}
        {(file || advisor || preview || result) && (
          <button type="button" className="rm-button rm-button--quiet" disabled={busy} onClick={reset}>
            {phase === 'done' ? 'Import another file' : 'Start over'}
          </button>
        )}
      </div>

      {busy && <ImportProgress progress={progress} />}

      {phase === 'ready' && preview && <ImportSummary response={preview} />}
      {phase === 'done' && result && <ImportSummary response={result} />}
    </div>
  );
};

export default LinkedInImportTab;
